// THE FIRST-LOGIN CONSENT GATE (Peter, 2026-08-28: "we need a consent the
// first time someone is logging in").
//
// Clickwrap that stands up: a signed-in user whose account has not accepted
// the CURRENT terms version sees this interstitial before any app content,
// and acceptance is an affirmative click recorded on their auth account
// (user_metadata: tos_accepted_version + tos_accepted_at) — user id,
// timestamp, and document version, which is the evidence set clickwrap
// case law asks for. The version is the documents' effective date, pinned
// by test against Terms.tsx itself so the two cannot drift: bumping the
// documents' effective date re-prompts every user exactly once.
//
// Storage is auth user_metadata rather than a table ON PURPOSE (2026-08-28:
// the east DB cutover is mid-flight and this repo lands no migration today).
// A durable public.terms_acceptance ledger is the filed follow-up; when it
// lands, this gate writes both.
//
// Fail-closed: if the acceptance write fails, the gate stays. The only exits
// are agreeing or signing out.
import { useState } from 'react';
import { PRODUCT_NAME } from '@/lib/brand';
import { supabase } from '@/lib/supabase';
import type { Session } from '@supabase/supabase-js';
import { isCell } from '@/lib/cell/config';
import { cellPost } from '@/lib/cell/api';
import { signOut } from '@/lib/auth';

// The effective date printed at the top of /terms and /privacy. The
// consent-version test reads Terms.tsx and fails the build if this drifts.
export const TERMS_VERSION = '2026-08-28';

export function hasAcceptedCurrentTerms(session: Session | null): boolean {
  return session?.user?.user_metadata?.tos_accepted_version === TERMS_VERSION;
}

export default function ConsentGate({
  session,
  children,
}: {
  session: Session | null;
  children: React.ReactNode;
}) {
  // Accepted-this-visit lives in state so the UI proceeds the moment the
  // write succeeds, without waiting for the auth listener to re-emit.
  const [acceptedNow, setAcceptedNow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (acceptedNow || hasAcceptedCurrentTerms(session)) return <>{children}</>;

  const agree = async () => {
    setSaving(true);
    setError(null);
    try {
      // CELL (M28): the acceptance is recorded in the cell (app.user_terms: user id, version, time) by
      // POST /v1/pro-hub/terms — the same evidence set, on Tenean's own database instead of auth metadata.
      const { error: err } = isCell()
        ? await cellPost('/v1/pro-hub/terms', { version: TERMS_VERSION }, { readInViewAs: false })
        : await supabase.auth.updateUser({
        data: {
          tos_accepted_version: TERMS_VERSION,
          tos_accepted_at: new Date().toISOString(),
        },
      });
      if (err) throw err;
      setAcceptedNow(true);
    } catch {
      setError('We could not record your agreement. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-fog grid place-items-center px-6">
      <div className="w-full max-w-md bg-white border border-brand-mist rounded-2xl p-8 text-center">
        <h1 className="font-display text-2xl text-brand-deep">{PRODUCT_NAME}</h1>
        <p className="mt-1 text-xs uppercase tracking-[0.18em] text-brand-slate/70">
          Before you continue
        </p>
        <p className="mt-5 text-sm text-brand-slate text-left">
          Please review the terms that cover your use of {PRODUCT_NAME}:
        </p>
        <ul className="mt-3 text-sm text-left list-disc pl-5 space-y-1">
          <li>
            <a href="/terms" target="_blank" rel="noreferrer" className="text-image-pink underline">
              Terms of Service
            </a>
          </li>
          <li>
            <a href="/privacy" target="_blank" rel="noreferrer" className="text-image-pink underline">
              Privacy Policy
            </a>
          </li>
        </ul>
        <p className="mt-4 text-sm text-brand-slate text-left">
          By continuing, you agree to the Terms of Service and acknowledge the Privacy Policy.
        </p>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        <button
          type="button"
          onClick={agree}
          disabled={saving}
          className="mt-6 w-full rounded-xl bg-image-pink text-white py-3 text-sm font-semibold disabled:opacity-50"
        >
          {saving ? 'One moment…' : 'Agree and continue'}
        </button>
        <button
          type="button"
          onClick={() => (isCell() ? void signOut() : supabase.auth.signOut({ scope: 'local' }))} /* local, never every device — decision d0aaa17a */
          className="mt-3 text-xs text-brand-slate underline"
        >
          I don&rsquo;t agree — sign me out
        </button>
      </div>
    </div>
  );
}
